import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

interface LiveBattle {
  id: string;
  title: string;
  genre?: string;
  liveKitRoom?: string;
}

interface LiveParticipant {
  id: string;
  username: string;
  role: 'JUDGE' | 'FINALIST' | 'AUDIENCE';
  avatarColor?: string;
  isVideoOn?: boolean;
  isMuted?: boolean;
}

interface ChatMessage {
  id: string;
  username: string;
  body: string;
  createdAt: string;
  role?: string;
}

const PARTICIPANT_COLORS = ['#C9A227', '#37B879', '#3B82F6', '#EF4444', '#A855F7', '#F59E0B', '#06B6D4', '#EC4899'];

function VideoSlot({ participant, label }: { participant?: LiveParticipant; label?: string }) {
  const name = participant?.username || label || 'Waiting…';
  const color = participant?.avatarColor || PARTICIPANT_COLORS[Math.floor(Math.random() * PARTICIPANT_COLORS.length)];
  const initials = name.slice(0, 2).toUpperCase();
  const isLive = participant?.isVideoOn;

  return (
    <div className="live-video-slot" title={name}>
      <div className="live-video-slot-inner" style={{ background: isLive ? '#0a0a0a' : `linear-gradient(135deg, ${color}22, ${color}44)` }}>
        {isLive ? (
          <div className="live-video-placeholder">
            <span className="live-video-cam-icon">📹</span>
            <span className="live-video-slot-name">{name}</span>
          </div>
        ) : (
          <div className="live-video-placeholder">
            <div className="live-video-avatar" style={{ background: color }}>{initials}</div>
            <span className="live-video-slot-name">{name}</span>
          </div>
        )}
        {participant && (
          <div className="live-video-slot-badge">
            {participant.role === 'JUDGE' && <span className="live-badge live-badge-judge">JUDGE</span>}
            {participant.role === 'FINALIST' && <span className="live-badge live-badge-finalist">FINALIST</span>}
            {participant.isMuted && <span className="live-badge live-badge-muted">🔇</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export function LiveVideoBar() {
  const [activeBattle, setActiveBattle] = useState<LiveBattle | null>(null);
  const [participants, setParticipants] = useState<LiveParticipant[]>([]);
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [viewerCount, setViewerCount] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  const checkLive = async () => {
    try {
      const res = await api.activeLive();
      if (res.active && res.battles && res.battles.length > 0) {
        const battle = res.battles[0];
        setActiveBattle({
          id: battle.id,
          title: battle.title || battle.battleTitle || 'Live Battle',
          genre: battle.genre,
          liveKitRoom: battle.liveKitRoom,
        });
        setViewerCount(battle.viewerCount || battle.participants?.length || 0);
        // Try to fetch live status (participants + chat)
        try {
          const status = await api.liveStatus(battle.id);
          if (status.participants) {setParticipants(status.participants);setViewerCount(status.participants.length);}
          if (status.chat) setChat(status.chat);
        } catch {
          // Live status endpoint may not be available yet — use placeholder data
          setParticipants([]);
          setChat([]);
        }
      } else {
        setActiveBattle(null);
        setParticipants([]);
        setChat([]);
      }
    } catch {
      // Silently fail — the bar just won't show
    }
  };

  useEffect(() => {
    checkLive();
    pollRef.current = setInterval(checkLive, 10000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chat]);

  if (!activeBattle) return null;

  const finalists = participants.filter((p) => p.role === 'FINALIST');
  const judges = participants.filter((p) => p.role === 'JUDGE');
  const audience = participants.filter((p) => p.role === 'AUDIENCE');

  // Build placeholder slots if no real participants yet
  const finalistSlots = finalists.length > 0
    ? finalists
    : Array.from({ length: 2 }, (_, i) => ({ id: `ph-f-${i}`, username: '', role: 'FINALIST' as const }));

  const judgeSlots = judges.length > 0
    ? judges
    : Array.from({ length: 3 }, (_, i) => ({ id: `ph-j-${i}`, username: '', role: 'JUDGE' as const }));

  const handleJoin = () => {
    navigate(`/app/live?battleId=${activeBattle.id}`);
  };

  return (
    <div className="live-video-bar" role="region" aria-label="Live battle video bar">
      {/* Collapsed bar — always visible */}
      <div className="live-video-bar-header" onClick={() => setExpanded(!expanded)} role="button" tabIndex={0}
           onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); }}>
        <div className="live-video-bar-title">
          <span className="live-pulse-dot" />
          <span className="live-badge-live">LIVE</span>
          <span className="live-video-bar-name">{activeBattle.title}</span>
          {activeBattle.genre && <span className="live-video-bar-genre">{activeBattle.genre}</span>}
        </div>
        <div className="live-video-bar-meta">
          <span className="live-viewer-count">
            👥 {viewerCount} {viewerCount === 1 ? 'viewer' : 'viewers'}
          </span>
          {audience.length > 0 && (
            <span className="live-audience-count">{audience.length} in room</span>
          )}
          <button className="btn btn-gold btn-sm live-video-bar-join" onClick={(e) => { e.stopPropagation(); handleJoin(); }}>
            Join Live →
          </button>
          <span className="live-video-bar-chevron">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="live-video-bar-expanded">
          {/* Participant video slots */}
          <div className="live-video-slots-section">
            <div className="live-video-slots-label">Finalists</div>
            <div className="live-video-slots-row">
              {finalistSlots.map((p, i) => (
                <VideoSlot key={p.id || i} participant={participants.find((x) => x.id === p.id)} label={p.username || `Finalist ${i + 1}`} />
              ))}
            </div>
            <div className="live-video-slots-label">Judges</div>
            <div className="live-video-slots-row">
              {judgeSlots.map((p, i) => (
                <VideoSlot key={p.id || i} participant={participants.find((x) => x.id === p.id)} label={p.username || `Judge ${i + 1}`} />
              ))}
            </div>
          </div>

          {/* Chat ticker */}
          <div className="live-chat-section">
            <div className="live-chat-header">
              <span>Live Chat</span>
              {chat.length > 0 && <span className="live-chat-count">{chat.length} messages</span>}
            </div>
            <div className="live-chat-ticker" ref={chatScrollRef}>
              {chat.length === 0 ? (
                <div className="live-chat-empty muted">No messages yet. Join the battle to chat.</div>
              ) : (
                chat.slice(-30).map((msg) => (
                  <div key={msg.id} className="live-chat-msg">
                    <span className="live-chat-user" style={{ color: msg.role === 'JUDGE' ? 'var(--iam-gold)' : msg.role === 'HOST' ? 'var(--iam-success)' : 'var(--text-dim)' }}>
                      {msg.username}
                    </span>
                    <span className="live-chat-body">{msg.body}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Action bar */}
          <div className="live-video-bar-actions">
            <button className="btn btn-gold btn-sm" onClick={handleJoin}>
              Join Live Battle →
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(false)}>
              Collapse
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
